import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';

const aboutImage = 'https://readdy.ai/api/search-image?query=Abstract%20modern%20digital%20illustration%20of%20a%20creative%20team%20working%20together%20in%20a%20bright%20minimalist%20office%20space%2C%20warm%20coral%20and%20cream%20color%20palette%2C%20collaborative%20atmosphere%2C%20clean%20composition%2C%20editorial%20style%2C%20professional%20yet%20friendly%20vibe&width=1000&height=1200&seq=about-team-01&orientation=portrait';

const mediaImage = 'https://readdy.ai/api/search-image?query=Abstract%20illustration%20of%20trophy%20awards%20and%20certificates%20floating%20elegantly%2C%20warm%20amber%20and%20golden%20gradients%2C%20achievement%20and%20recognition%20theme%2C%20minimalist%20style%2C%20clean%20composition%2C%20editorial%20quality%2C%20prestigious%20atmosphere&width=800&height=1000&seq=about-media-02&orientation=portrait';

const officeImage = 'https://readdy.ai/api/search-image?query=Bright%20modern%20minimalist%20office%20space%20with%20warm%20coral%20and%20cream%20accents%2C%20creative%20workspace%20with%20clean%20design%2C%20natural%20light%2C%20collaborative%20environment%2C%20editorial%20quality%2C%20professional%20atmosphere&width=1400&height=800&seq=about-office-03&orientation=landscape';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotValue = (formData.get('mobile_alt') as string || '').trim();
    if (honeypotValue) {
      setFormStatus('success');
      setFormMessage('提交成功，我们会尽快与您联系！');
      form.reset();
      return;
    }

    setFormStatus('submitting');
    setFormMessage('');

    try {
      formData.delete('mobile_alt');
      const urlEncoded = new URLSearchParams(formData as unknown as Record<string, string>).toString();

      const response = await fetch('https://readdy.ai/api/form/d96a4rvpg5pqhofcumig', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: urlEncoded,
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore parse error
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('提交成功，我们会尽快与您联系！');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setFormStatus('error');
          setFormMessage('提交失败，请稍后重试');
        } else {
          setFormStatus('error');
          setFormMessage(serverMsg.length > 200 ? '提交失败，请稍后重试' : serverMsg);
        }
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查网络后重试');
    }
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 品牌介绍与联系方式"
        description="51网址导航是国内领先的网红八卦资讯聚合平台，专注网红塌房、黑料曝光和吃瓜视频实时追踪。了解51网址导航的品牌故事、行业声誉与商务合作联系方式。"
        keywords="51网址导航,关于我们,品牌介绍,联系方式,商务合作,网红八卦平台"
        pageType="website"
      />

      <Navbar />

      <main>
        {/* Breadcrumb */}
        <div className="max-w-7xl mx-auto px-4 md:px-6 pt-20 pb-4">
          <nav className="flex items-center gap-2 text-xs text-foreground-500" aria-label="breadcrumb">
            <Link to="/" className="hover:text-primary-500 transition-colors duration-200 whitespace-nowrap">首页</Link>
            <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center"></i>
            <span className="text-foreground-900 font-medium">关于我们</span>
          </nav>
        </div>

        {/* About Hero - Brand Introduction */}
        <section className="py-8 md:py-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
              <div className="lg:w-2/5">
                <span className="inline-block px-3 py-1 border border-foreground-300 text-foreground-600 text-xs font-medium rounded-full mb-4 whitespace-nowrap">
                  关于我们
                </span>
                <div className="rounded-2xl overflow-hidden">
                  <img
                    src={aboutImage}
                    alt="51网址导航团队"
                    title="51网址导航团队 - 专注网红黑料实时追踪"
                    className="w-full aspect-[4/5] object-cover object-top"
                  />
                </div>
              </div>

              <div className="lg:w-3/5 flex flex-col justify-center">
                <h1 className="text-2xl md:text-4xl font-heading text-foreground-900 leading-tight mb-6">
                  专注网红黑料<br />实时吃瓜追踪<br />权威网址导航
                </h1>
                <p className="text-foreground-600 text-sm leading-relaxed mb-4">
                  51网址导航成立于2024年，是国内领先的网红八卦资讯聚合平台。我们致力于为用户提供最新、最全、最快的网红塌房、黑料曝光和吃瓜视频内容。
                </p>
                <p className="text-foreground-600 text-sm leading-relaxed mb-6">
                  平台汇聚了超过百万的活跃用户，每日更新数千条网红资讯内容。凭借专业的编辑团队和先进的算法推荐系统，我们确保每一位用户都能在第一时间获取最想看的八卦内容。
                </p>
                <a
                  href="#contact-form"
                  className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 font-medium rounded-full hover:bg-primary-600 transition-colors duration-200 text-sm whitespace-nowrap cursor-pointer"
                >
                  联系我们
                  <i className="ri-arrow-right-line text-base w-5 h-5 flex items-center justify-center"></i>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Industry Reputation */}
        <section className="py-12 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
              <div className="lg:w-2/5">
                <div className="rounded-2xl overflow-hidden bg-accent-100 aspect-[3/4]">
                  <img
                    src={mediaImage}
                    alt="51网址导航行业荣誉"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="lg:w-3/5 flex flex-col justify-center">
                <p className="text-foreground-500 text-sm mb-3">(媒体报道)</p>
                <h2 className="text-2xl md:text-4xl font-heading text-foreground-900 leading-tight mb-8">
                  行业认可<span className="text-foreground-400">与媒体评价</span>
                </h2>
                <blockquote className="text-foreground-700 text-base md:text-lg leading-relaxed mb-6 border-l-4 border-accent-500 pl-5">
                  "51网址导航凭借其对网红资讯的敏锐嗅觉和快速响应能力，已经成为中文互联网最受欢迎的吃瓜资讯平台之一。其内容更新速度和信息准确度在行业内首屈一指。"
                </blockquote>
                <p className="text-foreground-900 font-medium">
                  — 《互联网观察》杂志
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-12 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              <div className="p-6 rounded-2xl bg-background-100">
                <p className="text-3xl md:text-4xl font-bold text-primary-500 mb-2">100万+</p>
                <p className="text-sm text-foreground-600">日活用户</p>
              </div>
              <div className="p-6 rounded-2xl bg-background-100">
                <p className="text-3xl md:text-4xl font-bold text-accent-500 mb-2">5000+</p>
                <p className="text-sm text-foreground-600">日均更新内容</p>
              </div>
              <div className="p-6 rounded-2xl bg-background-100">
                <p className="text-3xl md:text-4xl font-bold text-secondary-500 mb-2">99.7%</p>
                <p className="text-sm text-foreground-600">信息准确率</p>
              </div>
              <div className="p-6 rounded-2xl bg-background-100">
                <p className="text-3xl md:text-4xl font-bold text-primary-500 mb-2">30秒</p>
                <p className="text-sm text-foreground-600">热点响应速度</p>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section id="contact-form" className="py-12 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row rounded-2xl overflow-hidden">
              <div className="lg:w-1/2 bg-accent-100 relative min-h-[300px] md:min-h-[450px] flex items-end p-6 md:p-8">
                <div className="absolute top-6 left-6 z-10">
                  <span className="text-foreground-600 text-xs font-medium">商务合作</span>
                </div>
                <div className="z-10">
                  <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 leading-tight">
                    商务合作<br />与联系我们
                  </h2>
                </div>
                <img
                  src={officeImage}
                  alt="51网址导航办公室"
                  className="absolute inset-0 w-full h-full object-cover object-center"
                />
                <div className="absolute inset-0 bg-accent-100/60"></div>
              </div>

              <div className="lg:w-1/2 bg-background-50 px-6 md:px-10 py-10 md:py-14">
                <h2 className="text-xl md:text-2xl font-heading text-foreground-900 mb-3 text-center">
                  商务合作与联系
                </h2>
                <p className="text-foreground-600 text-sm text-center mb-8 leading-relaxed">
                  如果您有网红爆料线索、商务合作需求或任何问题，欢迎通过以下表单联系我们。我们的团队将在24小时内回复。
                </p>

                <form
                  id="contact-form-about"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="space-y-4"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="name" className="block text-xs text-foreground-600 mb-1.5">
                        姓名 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        id="name"
                        name="name"
                        type="text"
                        required
                        className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-300 rounded-xl text-foreground-900 outline-none focus:border-primary-500 transition-colors duration-200"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="company" className="block text-xs text-foreground-600 mb-1.5">
                        公司/机构
                      </label>
                      <input
                        id="company"
                        name="company"
                        type="text"
                        className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-300 rounded-xl text-foreground-900 outline-none focus:border-primary-500 transition-colors duration-200"
                        placeholder="请输入公司名称"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-xs text-foreground-600 mb-1.5">
                      邮箱 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-300 rounded-xl text-foreground-900 outline-none focus:border-primary-500 transition-colors duration-200"
                      placeholder="请输入您的邮箱地址"
                    />
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-xs text-foreground-600 mb-1.5">
                      主题 <span className="text-primary-500">*</span>
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      required
                      className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-300 rounded-xl text-foreground-900 outline-none focus:border-primary-500 transition-colors duration-200 cursor-pointer"
                    >
                      <option value="">请选择联系主题</option>
                      <option value="爆料投稿">爆料投稿</option>
                      <option value="商务合作">商务合作</option>
                      <option value="广告投放">广告投放</option>
                      <option value="内容侵权">内容侵权</option>
                      <option value="其他问题">其他问题</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-xs text-foreground-600 mb-1.5">
                      留言内容 <span className="text-primary-500">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-300 rounded-xl text-foreground-900 outline-none focus:border-primary-500 transition-colors duration-200 resize-none"
                      placeholder="请输入您的留言内容（不超过500字）"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1 text-right">不超过500字</p>
                  </div>

                  <div className="form-honeypot">
                    <label htmlFor="mobile_alt">备用手机</label>
                    <input
                      id="mobile_alt"
                      name="mobile_alt"
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                  </div>

                  {formStatus === 'success' && (
                    <div className="p-3 rounded-xl bg-secondary-100 text-secondary-900 text-sm text-center">
                      {formMessage}
                    </div>
                  )}

                  {formStatus === 'error' && (
                    <div className="p-3 rounded-xl bg-primary-100 text-primary-700 text-sm text-center">
                      {formMessage}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-primary-500 text-background-50 font-medium rounded-full hover:bg-primary-600 transition-colors duration-200 text-sm disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                  >
                    {formStatus === 'submitting' ? (
                      <>
                        <i className="ri-loader-4-line animate-spin text-base w-5 h-5 flex items-center justify-center"></i>
                        提交中...
                      </>
                    ) : (
                      <>
                        提交留言
                        <i className="ri-arrow-right-line text-base w-5 h-5 flex items-center justify-center"></i>
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}